import type { Coords } from './use-current-location';

/** Normalize a free-form query so trivially-different spellings ("  New York ", "new york")
 *  hit the same cache entry. Case-insensitive is safe — Google geocoding ignores case. */
const normalizeQuery = (address: string): string =>
  address.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Wrap a forward-geocoder with a process-lifetime in-memory cache keyed by the normalized
 * query, so repeated searches for the same address (re-typing, clearing + retyping, revisiting
 * the screen) don't re-hit the paid Google Geocoding API.
 *
 * - Caches the in-flight promise, so two identical lookups fired back-to-back share one request.
 * - Keeps successful results (a resolved address doesn't move), but evicts misses/errors so a
 *   transient network failure — or an address that only later resolves — can be retried.
 */
export function withGeocodeCache(
  resolve: (query: string) => Promise<Coords | null>,
): (address: string) => Promise<Coords | null> {
  const cache = new Map<string, Promise<Coords | null>>();

  return (address: string): Promise<Coords | null> => {
    const query = normalizeQuery(address);
    if (!query) return Promise.resolve(null);

    const cached = cache.get(query);
    if (cached) return cached;

    const pending = resolve(query)
      .then(result => {
        if (!result) cache.delete(query);
        return result;
      })
      .catch(error => {
        cache.delete(query);
        throw error;
      });

    cache.set(query, pending);
    return pending;
  };
}
